import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, LANDSCAPE, ARCHITECTURE, SPINE, PHASING, CLOSE } from "./script";

// ── Shared layout helpers ──────────────────────────────────────────────────
const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 26,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number; color?: string }> = ({
  children,
  size = 88,
  color = COLORS.text,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/** Fade+rise a block in over [a,b] (scene-local frames). */
const useReveal = (a: number, b: number) =>
  interpolate(useCurrentFrame(), [a, b], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

// A status pill, used wherever a phase/foundation claim appears on screen, so the
// honesty label is never separated from the claim (series core rule).
type StatusKind = "shipped" | "flag" | "roadmap";
const STATUS_META: Record<StatusKind, { label: string; color: string }> = {
  shipped: { label: "SHIPPED", color: COLORS.green },
  flag: { label: "BEHIND A FLAG", color: COLORS.amber },
  roadmap: { label: "ROADMAP", color: COLORS.dim },
};
const StatusPill: React.FC<{ kind: StatusKind }> = ({ kind }) => {
  const m = STATUS_META[kind];
  return (
    <span
      style={{
        fontFamily: FONTS.mono,
        fontSize: 15,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: m.color,
        border: `1px solid ${m.color}66`,
        borderRadius: 6,
        padding: "3px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </span>
  );
};

// ── 01 · HOOK · THE SHIFT ────────────────────────────────────────────────────
// Left: a read-only answer (text → table). Right: the same answer upgraded with
// inputs, a control, and an Approve+Send button — UI that carries actions.
export const Scene01Hook: React.FC = () => {
  const showsIn = useReveal(20, 60);
  const arrowIn = useReveal(150, 185);
  const buildsIn = useReveal(190, 240);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 118 }}>
        <Kicker text="action-oriented dynamic UI · planning" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 30, alignItems: "center" }}>
          {/* BEFORE — read-only */}
          <div style={{ opacity: showsIn, width: 560 }}>
            <Panel accent="cyan" title="agent → user · shows data">
              <div style={{ fontFamily: FONTS.body, fontSize: 24, color: COLORS.text, lineHeight: 1.5 }}>
                Here is the draft email.
              </div>
              <div
                style={{
                  marginTop: 14,
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 8,
                  padding: "12px 14px",
                  fontFamily: FONTS.mono,
                  fontSize: 18,
                  color: COLORS.dim,
                  lineHeight: 1.7,
                  background: COLORS.panel2,
                }}
              >
                <div>To: customer@acme.com</div>
                <div>Subject: weekly update</div>
                <div style={{ color: COLORS.text }}>— body —</div>
              </div>
              <div style={{ fontFamily: FONTS.mono, fontSize: 16, color: COLORS.cyan, marginTop: 14 }}>
                read-only · nothing to click
              </div>
            </Panel>
          </div>

          {/* arrow */}
          <div
            style={{
              opacity: arrowIn,
              fontFamily: FONTS.display,
              fontWeight: 700,
              fontSize: 52,
              color: COLORS.green,
            }}
          >
            →
          </div>

          {/* AFTER — action-oriented */}
          <div style={{ opacity: buildsIn, width: 560 }}>
            <Panel accent="green" title="agent → user · builds actions">
              <div style={{ fontFamily: FONTS.body, fontSize: 24, color: COLORS.text, lineHeight: 1.5 }}>
                Edit it, then approve.
              </div>
              <textarea
                readOnly
                value={"To: customer@acme.com\nSubject: weekly update\n— editable body —"}
                style={{
                  marginTop: 14,
                  width: "100%",
                  height: 88,
                  resize: "none",
                  border: `1px solid ${COLORS.green}77`,
                  borderRadius: 8,
                  padding: "12px 14px",
                  fontFamily: FONTS.mono,
                  fontSize: 17,
                  color: COLORS.text,
                  background: COLORS.panel2,
                  lineHeight: 1.6,
                }}
              />
              <div style={{ display: "flex", gap: 12, marginTop: 14 }}>
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 18,
                    color: COLORS.dim,
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: 8,
                    padding: "8px 16px",
                  }}
                >
                  Edit
                </div>
                <div
                  style={{
                    fontFamily: FONTS.mono,
                    fontSize: 18,
                    color: COLORS.bg,
                    background: COLORS.green,
                    borderRadius: 8,
                    padding: "8px 16px",
                    fontWeight: 600,
                  }}
                >
                  Approve + Send
                </div>
              </div>
            </Panel>
          </div>
        </div>
        <div
          style={{
            opacity: useReveal(250, 290),
            fontFamily: FONTS.mono,
            fontSize: 20,
            color: COLORS.dim,
            marginTop: 40,
          }}
        >
          internally: <span style={{ color: COLORS.cyan }}>A2UI v2</span>{"  "}·{"  "}
          a planning + phased-build episode
        </div>
      </AbsoluteFill>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

// ── 02 · THE LANDSCAPE VERDICT ───────────────────────────────────────────────
// keep / borrow / adopt / avoid — the four-column standards stance.
const VERDICT: { tag: string; color: string; items: { name: string; note: string }[] }[] = [
  {
    tag: "KEEP",
    color: COLORS.green,
    items: [
      { name: "EAKC-native catalog", note: "fail-closed types + validator + renderer" },
      { name: "blackboard widget store", note: "tenant-scoped, already shipped" },
    ],
  },
  {
    tag: "BORROW",
    color: COLORS.cyan,
    items: [
      { name: "A2UI v0.9 wire shape", note: "flat adjacency-list · portable" },
      { name: "AG-UI event taxonomy", note: "in-app SSE model" },
    ],
  },
  {
    tag: "ADOPT",
    color: COLORS.lime,
    items: [
      { name: "MCP Apps", note: "EAKC IS an MCP server · postMessage" },
      { name: "react-grid-layout", note: "dashboard canvas · JSON layout" },
    ],
  },
  {
    tag: "AVOID",
    color: COLORS.red,
    items: [
      { name: "Thesys / CopilotKit runtime", note: "captures inference / hosting" },
      { name: "Vercel RSC streamUI", note: "paused / experimental" },
    ],
  },
];
export const Scene02Landscape: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.landscape.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 96, padding: "96px 70px 0" }}>
        <Kicker text="the landscape verdict · standards stance" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 18, alignItems: "stretch", width: "100%", maxWidth: 1760 }}>
          {VERDICT.map((col, i) => {
            const op = interpolate(frame, [40 + i * 28, 80 + i * 28], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={col.tag}
                style={{
                  opacity: op,
                  transform: `translateY(${(1 - op) * 18}px)`,
                  flex: 1,
                  background: COLORS.panel,
                  border: `1px solid ${COLORS.line}`,
                  borderTop: `3px solid ${col.color}`,
                  borderRadius: 10,
                  padding: "18px 18px 20px",
                }}
              >
                <div
                  style={{
                    fontFamily: FONTS.display,
                    fontWeight: 700,
                    fontSize: 32,
                    letterSpacing: "0.04em",
                    color: col.color,
                    marginBottom: 16,
                  }}
                >
                  {col.tag}
                </div>
                {col.items.map((it) => (
                  <div key={it.name} style={{ marginBottom: 16 }}>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 20, color: COLORS.text, lineHeight: 1.25 }}>
                      {it.name}
                    </div>
                    <div style={{ fontFamily: FONTS.body, fontSize: 16, color: COLORS.dim, marginTop: 4, lineHeight: 1.35 }}>
                      {it.note}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div
          style={{
            opacity: useReveal(220, 260),
            fontFamily: FONTS.mono,
            fontSize: 21,
            color: COLORS.text,
            background: COLORS.panel2,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 10,
            padding: "14px 28px",
            marginTop: 30,
          }}
        >
          net: <span style={{ color: COLORS.green }}>component layer EAKC-native</span> ·{" "}
          <span style={{ color: COLORS.lime }}>transport = MCP Apps + AG-UI-modeled SSE</span> ·{" "}
          <span style={{ color: COLORS.cyan }}>dashboard = react-grid-layout</span>
        </div>
      </AbsoluteFill>
      <Caption lines={LANDSCAPE} accent="cyan" />
    </AbsoluteFill>
  );
};

// ── 03 · THE ARCHITECTURE ────────────────────────────────────────────────────
// One descriptor → 3 surfaces → ONE action spine → 4 transports.
const SPINE_PIPE: PipeNode[] = [
  { label: "produced once", value: "descriptor", sub: "by the LLM", accent: "cyan" },
  { label: "3 surfaces", value: "render", sub: "deterministic", accent: "green" },
  { label: "user clicks", value: "1 event", sub: "no regen", accent: "lime" },
  { label: "server", value: "action spine", sub: "no LLM", accent: "amber" },
];
const SURFACES: { name: string; note: string; color: string; status: StatusKind }[] = [
  { name: "missions", note: "emit_widget step (first-class)", color: COLORS.green, status: "shipped" },
  { name: "chat", note: "interactive artifact + SSE", color: COLORS.cyan, status: "roadmap" },
  { name: "dashboard builder", note: "react-grid-layout canvas", color: COLORS.lime, status: "roadmap" },
];
const TRANSPORTS: { name: string; note: string }[] = [
  { name: "in-app", note: "AG-UI-modeled SSE" },
  { name: "external", note: "MCP Apps · postMessage" },
  { name: "embed", note: "signed-token iframe" },
  { name: "portable", note: "A2UI v0.9 wire" },
];
export const Scene03Architecture: React.FC = () => {
  const frame = useCurrentFrame();
  const surfacesIn = useReveal(180, 230);
  const transportsIn = useReveal(360, 410);
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.architecture.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 90, padding: "90px 70px 0" }}>
        <Kicker text="one contract · three surfaces · one spine · four transports" color={COLORS.green} />
        <div style={{ width: 1760 }}>
          <PipelineGraph nodes={SPINE_PIPE} startFrame={8} stepFrames={26} nodeWidth={226} />
        </div>

        {/* three surfaces */}
        <div style={{ opacity: surfacesIn, display: "flex", gap: 16, marginTop: 46, width: "100%", maxWidth: 1500 }}>
          {SURFACES.map((s) => (
            <div
              key={s.name}
              style={{
                flex: 1,
                background: COLORS.panel,
                border: `1px solid ${COLORS.line}`,
                borderLeft: `4px solid ${s.color}`,
                borderRadius: 8,
                padding: "14px 18px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ fontFamily: FONTS.mono, fontSize: 23, color: s.color }}>{s.name}</span>
                <StatusPill kind={s.status} />
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 16, color: COLORS.dim, marginTop: 6 }}>{s.note}</div>
            </div>
          ))}
        </div>

        {/* four transports */}
        <div style={{ opacity: transportsIn, display: "flex", gap: 14, marginTop: 22, width: "100%", maxWidth: 1500 }}>
          {TRANSPORTS.map((t, i) => {
            const op = interpolate(frame, [360 + i * 18, 400 + i * 18], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={t.name}
                style={{
                  opacity: op,
                  flex: 1,
                  background: COLORS.panel2,
                  border: `1px solid ${COLORS.line}`,
                  borderRadius: 8,
                  padding: "12px 16px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontFamily: FONTS.mono, fontSize: 19, color: COLORS.text }}>{t.name}</div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim, marginTop: 4 }}>{t.note}</div>
              </div>
            );
          })}
        </div>
        <div
          style={{
            opacity: transportsIn,
            fontFamily: FONTS.body,
            fontSize: 20,
            color: COLORS.dim,
            marginTop: 22,
            textAlign: "center",
            maxWidth: 1360,
          }}
        >
          Refresh = new data into existing widgets — never a regeneration. The model authors the descriptor once.
        </div>
      </AbsoluteFill>
      <Caption lines={ARCHITECTURE} accent="green" />
    </AbsoluteFill>
  );
};

// ── 04 · THE ACTION SPINE + SAFETY ───────────────────────────────────────────
// The real action-contract.ts shape (grounded) + the four safety rules.
const CONTRACT_CODE = `// action-contract.ts · PURE · no DB, no LLM
// the 7 tiers; read/refresh/filter run, the rest gate
export const ACTION_KINDS = ['read','refresh','filter',
  'write','send','delete','external'] as const;

export const GUARDED_KINDS: ActionKind[] =
  ['write','send','delete','external'];

export interface ActionDef {
  id: string;            // referenced by component.action.id
  kind: ActionKind;      // tier → drives the confirm policy
  handler: string;       // allowlisted server handler key
  canonical_label: string; // SERVER-derived confirm text
}                        //   NEVER the model's string (LITL defense)

// the dispatcher result — deterministic, no regeneration
type DispatchResult =
  | { status: 'executed'; descriptor?: unknown }
  | { status: 'needs_confirm'; confirmToken: string;
      canonical_label: string }
  | { status: 'rejected'; reason: string }
  | { status: 'disabled'; reason: string };`;
export const Scene04Spine: React.FC = () => {
  const frame = useCurrentFrame();
  const rules = [
    { k: "renderer EMITS, never executes", d: "no handler runs client-side · no dangerouslySet*", c: COLORS.green },
    { k: "registry FROZEN at plan-time", d: "actionId ∉ registry → reject", c: COLORS.cyan },
    { k: "tiered, server-derived confirm", d: "write/send/delete/external → challenge", c: COLORS.amber },
    { k: "no LLM in render/refresh loop", d: "the $55-runaway defense", c: COLORS.magenta },
  ];
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.spine.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 80 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
          <Kicker text="the action spine + safety · src/lib/agents/ui/action-contract.ts" color={COLORS.amber} />
          <div style={{ marginBottom: 26 }}>
            <StatusPill kind="flag" />
          </div>
        </div>
        <div style={{ display: "flex", gap: 34, alignItems: "flex-start" }}>
          <CodePanel
            code={CONTRACT_CODE}
            language="tsx"
            filename="src/lib/agents/ui/action-contract.ts"
            highlightLines={[3, 4, 13, 19, 20, 21]}
            revealFrames={210}
            fontSize={18}
            width={1020}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 4 }}>
            {rules.map((r, i) => {
              const op = interpolate(frame, [150 + i * 56, 190 + i * 56], [0, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              });
              return (
                <div key={r.k} style={{ opacity: op }}>
                  <Callout label={`rule ${i + 1}`} accent="amber" width={440}>
                    <div style={{ fontFamily: FONTS.body, fontSize: 21, color: COLORS.text, lineHeight: 1.4 }}>
                      {r.k}
                    </div>
                    <div style={{ fontFamily: FONTS.mono, fontSize: 15, color: COLORS.dim, marginTop: 4 }}>
                      {r.d}
                    </div>
                  </Callout>
                </div>
              );
            })}
            <div style={{ opacity: useReveal(400, 440), marginTop: 6 }}>
              <Panel accent="green" title="dormant by default" width={440}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.text, lineHeight: 1.5 }}>
                  <span style={{ color: COLORS.amber }}>A2UI_ACTIONS_ENABLED</span> = off
                  <div style={{ color: COLORS.dim, fontSize: 15, marginTop: 6 }}>
                    guarded actions return &#123; status: 'disabled' &#125; · deploy-safe
                  </div>
                </div>
              </Panel>
            </div>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={SPINE} accent="amber" />
    </AbsoluteFill>
  );
};

// ── 05 · THE PHASING ─────────────────────────────────────────────────────────
// Each phase labelled on screen with its real status (honesty guardrail).
const PHASES: {
  id: string;
  title: string;
  detail: string;
  status: StatusKind;
  color: string;
}[] = [
  { id: "P0", title: "durable, self-selectable widgets", detail: "emit_widget first-class plan field · commit 87a2d85a", status: "shipped", color: COLORS.green },
  { id: "—", title: "read-only A2UI foundation", detail: "11-type catalog · fail-closed validator · renderer · signed-token embed", status: "shipped", color: COLORS.green },
  { id: "P1", title: "action spine + first action (HITL)", detail: "action contract · 7 tiers · action_events audit · default OFF", status: "flag", color: COLORS.amber },
  { id: "P2", title: "chat surface", detail: "SSE interactive branch + inline render + action routing", status: "roadmap", color: COLORS.dim },
  { id: "P3", title: "dashboard builder", detail: "dashboards table (RLS) · react-grid-layout · /admin/dashboards", status: "roadmap", color: COLORS.dim },
  { id: "P4", title: "catalog + reach", detail: "chart/KPI/image · MCP Apps reach · action-quality eval gate", status: "roadmap", color: COLORS.dim },
  { id: "P5", title: "polish", detail: "themes · export · wallboard · alert widgets · canvas direct-edit", status: "roadmap", color: COLORS.dim },
];
export const Scene05Phasing: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.phasing.duration} />
      <AbsoluteFill style={{ ...center, justifyContent: "flex-start", paddingTop: 84 }}>
        <Kicker text="the phasing · status labelled on screen" color={COLORS.amber} />
        <div style={{ display: "flex", flexDirection: "column", gap: 9, width: 1340 }}>
          {PHASES.map((p, i) => {
            const op = interpolate(frame, [40 + i * 22, 72 + i * 22], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            return (
              <div
                key={p.id + p.title}
                style={{
                  opacity: op,
                  transform: `translateX(${(1 - op) * -18}px)`,
                  display: "flex",
                  alignItems: "center",
                  gap: 18,
                  background: COLORS.panel,
                  border: `1px solid ${COLORS.line}`,
                  borderLeft: `4px solid ${p.color}`,
                  borderRadius: 8,
                  padding: "12px 22px",
                }}
              >
                <span
                  style={{
                    fontFamily: FONTS.display,
                    fontWeight: 700,
                    fontSize: 28,
                    color: p.color,
                    width: 52,
                    flexShrink: 0,
                  }}
                >
                  {p.id}
                </span>
                <span style={{ flex: 1 }}>
                  <span style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.text }}>{p.title}</span>
                  <div style={{ fontFamily: FONTS.body, fontSize: 16, color: COLORS.dim, marginTop: 3 }}>
                    {p.detail}
                  </div>
                </span>
                <StatusPill kind={p.status} />
              </div>
            );
          })}
        </div>
        <div
          style={{
            opacity: useReveal(330, 370),
            fontFamily: FONTS.mono,
            fontSize: 19,
            color: COLORS.dim,
            marginTop: 26,
            textAlign: "center",
            maxWidth: 1340,
          }}
        >
          Definition of Shipped: a phase flips to{" "}
          <span style={{ color: COLORS.green }}>green</span> only when its end-to-end behavior is
          observable without one more code change.
        </div>
      </AbsoluteFill>
      <Caption lines={PHASING} accent="amber" />
    </AbsoluteFill>
  );
};

// ── 06 · CLOSE ───────────────────────────────────────────────────────────────
export const Scene06Close: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.close.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={86}>
          Measure. Ground.{" "}
          <span style={{ color: COLORS.green }}>Gate.</span>
        </Big>
        <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 40, textAlign: "center", maxWidth: 1340 }}>
          Read-only widgets ship today. The action spine waits behind a flag for its first verified run. The rest is an honest roadmap.
        </div>
        <div style={{ display: "flex", gap: 22, marginTop: 40, alignItems: "center" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <StatusPill kind="shipped" />
            <span style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>P0 + foundation</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <StatusPill kind="flag" />
            <span style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>P1 spine</span>
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <StatusPill kind="roadmap" />
            <span style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>P2–P5</span>
          </span>
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 22, letterSpacing: "0.22em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · build in public · <span style={{ color: COLORS.cyan }}>A2UI v2</span>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={CLOSE} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
